import { collectionGroup, doc, query, where, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestoreInstance } from './firestore';
import { createBooking, deleteBooking, updateBooking } from './gigs';
import { getPerson, getMyPerson } from './people';
import { getRateCard } from './rateCards';
import type { Booking, CancellationFeeStatus, Position, RateEntry } from '@gigdriver/domain';
import { resolveAssignRate, isValidBookingTransition } from '@gigdriver/domain';
import { fromFirestore } from './serde';

// Places a candidate into a Position as a DRAFT — no notification is sent
// (onOfferCreated only reacts once the state becomes OFFERED). `callIds`
// is the subset of the position's own callIds this person is committing to
// right now; may be empty for a call-less/day-scoped assignment (calls can
// be linked later via setBookingCallIds, with no re-offer required). `rate`
// overrides the position's own rate for this person — omit to use the
// position's rate as-is.
export async function assignPerson(
  orgId: string,
  gigId: string,
  position: Position,
  personId: string,
  rate?: RateEntry,
  callIds: string[] = [],
): Promise<string> {
  const person = await getPerson(orgId, personId);
  if (!person) throw new Error('Person not found');

  return createBooking(orgId, gigId, position.id, {
    personId,
    userId: person.userId ?? undefined,
    positionId: position.id,
    gigId,
    orgId,
    state: 'DRAFT',
    tier: 0,
    callIds: callIds.filter(id => position.callIds.includes(id)),
    offeredAt: new Date(),
    rate: rate ?? position.rate,
    rateType: position.rateType,
    venueId: position.venueId,
    scopeDate: position.scopeDate,
    notes: '',
  });
}

// A member (e.g. `staff`) self-requesting an open position — the caller
// side of "request to fill". Resolves their own linked roster record and
// creates a self-targeted DRAFT booking via the same assignPerson path a
// coordinator's assignment uses, requesting the position's full call scope;
// the firestore.rules staff-create clause requires exactly this shape
// (DRAFT, userId == the caller).
export async function requestToFill(orgId: string, gigId: string, userId: string, position: Position): Promise<string> {
  const person = await getMyPerson(orgId, userId);
  if (!person) throw new Error('No roster record linked to your account yet — ask an admin to add you.');
  const rateCard = position.rateCardId ? await getRateCard(orgId, position.rateCardId) : null;
  const rate: RateEntry = {
    amount: resolveAssignRate(person, position, rateCard ?? undefined),
    currency: position.rate.currency,
  };
  return assignPerson(orgId, gigId, position, person.id, rate, position.callIds);
}

function bookingRef(orgId: string, gigId: string, booking: Pick<Booking, 'positionId' | 'id'>) {
  return doc(
    getFirestoreInstance(),
    'organizations',
    orgId,
    'gigs',
    gigId,
    'positions',
    booking.positionId,
    'bookings',
    booking.id,
  );
}

// Transitions a DRAFT booking to OFFERED, firing the notification/email
// (onOfferCreated). A single-document update — a Booking is already the
// whole offer now (one person, one position, its own callIds), so there
// are no group siblings to fan out to.
export async function sendOffer(gigId: string, booking: Booking): Promise<void> {
  await updateDoc(bookingRef(booking.orgId, gigId, booking), { state: 'OFFERED', updatedAt: serverTimestamp() });
}

// Removes a draft assignment before an offer is sent. Only meaningful while
// the booking is still DRAFT — a DRAFT has no calendar invites and no
// history worth keeping, so a hard delete is fine here. Anything past that
// (OFFERED/ACCEPTED/BOOKED) must go through cancelBooking instead, which
// keeps the record (and, for an accepted commitment, sends real calendar
// cancellations and can carry a fee) rather than silently disappearing.
export async function unassignPerson(gigId: string, booking: Booking): Promise<void> {
  if (booking.state !== 'DRAFT') {
    throw new Error(`Only a draft assignment can be unassigned — use cancelBooking for a ${booking.state} booking.`);
  }
  await deleteBooking(booking.orgId, gigId, booking.positionId, booking.id);
}

export interface CancelBookingInput {
  // Auth uid of the coordinator cancelling this booking.
  cancelledBy: string;
  reason?: string;
  // Omit entirely for no fee. When set with no feeStatus, defaults to OWED
  // (deciding to record a fee at all should default to it actually being
  // owed — the coordinator waives it explicitly, not by omission).
  fee?: RateEntry;
  feeStatus?: CancellationFeeStatus;
}

// Cancels an OFFERED/ACCEPTED/BOOKED booking — transitions it to CANCELLED
// instead of deleting the doc, so it stays visible (with its rate, reason,
// and fee) on the gig's Staffing Budget, in the candidate's own My Offers,
// and in their booking history, the same way a DECLINED booking already
// persists forever today.
//
// This MUST be a single atomic write containing both `state` and
// `callIds: []` together — never split into two updates. onBookingCallsChanged
// (the Cloud Function that re-syncs calendar invites when callIds changes)
// only acts when a booking's state is ACCEPTED; writing state and callIds
// separately would let that trigger fire on the callIds-only write, send
// its own CANCEL emails, AND overwrite `linkedCalls` to [] before the
// state-change trigger runs — destroying the history snapshot this whole
// feature depends on for the gig/offers/history views. One write keeps that
// trigger a no-op here (after.state is already 'CANCELLED') while
// onBookingStateChanged's own cancelled branch sends the real cancellations
// from the still-intact linkedCalls snapshot.
export async function cancelBooking(orgId: string, gigId: string, booking: Booking, input: CancelBookingInput): Promise<void> {
  if (!isValidBookingTransition(booking.state, 'CANCELLED')) {
    throw new Error(`Cannot cancel a booking in state ${booking.state}.`);
  }
  const feeStatus: CancellationFeeStatus | undefined = input.fee ? (input.feeStatus ?? 'OWED') : undefined;
  await updateDoc(bookingRef(orgId, gigId, booking), {
    state: 'CANCELLED',
    callIds: [],
    cancelledAt: serverTimestamp(),
    cancelledBy: input.cancelledBy,
    cancellationReason: input.reason || undefined,
    cancellationFee: input.fee,
    cancellationFeeStatus: feeStatus,
    updatedAt: serverTimestamp(),
  });
}

export async function respondToOffer(
  orgId: string,
  gigId: string,
  posId: string,
  bookingId: string,
  accept: boolean,
): Promise<void> {
  const update = accept
    ? { state: 'ACCEPTED' as const, acceptedAt: serverTimestamp(), updatedAt: serverTimestamp() }
    : { state: 'DECLINED' as const, updatedAt: serverTimestamp() };

  // The offered person only touches their own booking doc. onBookingStateChanged
  // owns the downstream effects (position FILLED/BOOKED, notifications, email).
  // Direct updateDoc (not the updateBooking helper) since it needs to accept
  // FieldValue sentinels (serverTimestamp()) that Partial<Booking> disallows.
  const ref = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', gigId, 'positions', posId, 'bookings', bookingId);
  await updateDoc(ref, update);
}

// Updates which of the parent Position's calls this booking covers,
// independent of `state` — never resets state, never re-triggers an offer.
// Filtered to the Position's own callIds so a booking can never reference a
// call outside its position's scope. onBookingCallsChanged (Cloud Function)
// reacts if the booking is already ACCEPTED, re-syncing the candidate's
// calendar invites to match.
export async function setBookingCallIds(
  orgId: string,
  gigId: string,
  position: Position,
  booking: Booking,
  callIds: string[],
): Promise<void> {
  await updateBooking(orgId, gigId, position.id, booking.id, {
    callIds: callIds.filter(id => position.callIds.includes(id)),
  });
}

// Every booking linked to the given auth uid, across all orgs (self-service).
// The caller filters by state.
export async function getPersonOffers(userId: string): Promise<Booking[]> {
  const q = query(collectionGroup(getFirestoreInstance(), 'bookings'), where('userId', '==', userId));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => fromFirestore<Booking>(d.data()));
}

// A roster person's full booking history within ONE org — every gig, every
// state (upcoming, past, declined, cancelled) — for a coordinator judging a
// cancellation fee or considering re-booking them elsewhere. Keyed on
// `personId`, not `userId`, so it works for the majority of roster members
// who have no linked Firebase Auth account (getPersonOffers, by contrast, is
// self-service and requires one). Sorted newest-first client-side — a
// single person's history is a small result set, not worth a composite
// index just to add an orderBy.
export async function getPersonBookings(orgId: string, personId: string): Promise<Booking[]> {
  const q = query(
    collectionGroup(getFirestoreInstance(), 'bookings'),
    where('orgId', '==', orgId),
    where('personId', '==', personId),
  );
  const snapshot = await getDocs(q);
  const bookings = snapshot.docs.map(d => fromFirestore<Booking>(d.data()));
  return bookings.sort((a, b) => new Date(b.offeredAt).getTime() - new Date(a.offeredAt).getTime());
}
